import { useCallback, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  fetchSavedSearches,
  createSavedSearch,
  deleteSavedSearch,
  updateSavedSearchCadence,
  fetchSavedSearchSettings,
  updateSavedSearchSettings,
  type SavedSearch,
  type SavedSearchFilters,
  type NotificationCadence,
  type SavedSearchSettings,
  type AddSavedSearchInput,
  readLocalPushPreference,
  writeLocalPushPreference,
} from '../api/savedSearches';
import { toast } from 'sonner';

const SAVED_SEARCHES_QUERY_KEY = ['candidate-saved-searches'];
const SAVED_SEARCH_SETTINGS_QUERY_KEY = ['candidate-saved-search-settings'];
const FALLBACK_CADENCE: NotificationCadence = 'weekly';

type UpdateCadenceInput = { id: string; cadence: NotificationCadence };

export function useSavedSearches() {
  const queryClient = useQueryClient();
  const [pushEnabled, setPushEnabledState] = useState<boolean>(() => readLocalPushPreference());

  const savedSearchesQuery = useQuery({
    queryKey: SAVED_SEARCHES_QUERY_KEY,
    queryFn: fetchSavedSearches,
    staleTime: 1000 * 30,
  });

  const settingsQuery = useQuery({
    queryKey: SAVED_SEARCH_SETTINGS_QUERY_KEY,
    queryFn: fetchSavedSearchSettings,
    staleTime: 1000 * 60 * 5,
  });

  const addSearchMutation = useMutation({
    mutationFn: async (input: AddSavedSearchInput) => {
      const defaultCadence = settingsQuery.data?.defaultCadence ?? FALLBACK_CADENCE;
      const payload: AddSavedSearchInput & { cadence: NotificationCadence } = {
        ...input,
        cadence: input.cadence ?? defaultCadence,
      };
      return createSavedSearch(payload);
    },
    onSuccess: (newSearch) => {
      queryClient.setQueryData<SavedSearch[]>(SAVED_SEARCHES_QUERY_KEY, (current = []) => [newSearch, ...current]);
    },
  });

  const removeSearchMutation = useMutation({
    mutationFn: deleteSavedSearch,
    onMutate: async (id: string) => {
      await queryClient.cancelQueries({ queryKey: SAVED_SEARCHES_QUERY_KEY });
      const previous = queryClient.getQueryData<SavedSearch[]>(SAVED_SEARCHES_QUERY_KEY);
      if (previous) {
        queryClient.setQueryData<SavedSearch[]>(
          SAVED_SEARCHES_QUERY_KEY,
          previous.filter((search) => search.id !== id),
        );
      }
      return { previous };
    },
    onError: (_error, _id, context) => {
      if (context?.previous) {
        queryClient.setQueryData(SAVED_SEARCHES_QUERY_KEY, context.previous);
      }
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: SAVED_SEARCHES_QUERY_KEY });
    },
  });

  const updateCadenceMutation = useMutation({
    mutationFn: ({ id, cadence }: UpdateCadenceInput) => updateSavedSearchCadence(id, cadence),
    onSuccess: (updatedSearch) => {
      queryClient.setQueryData<SavedSearch[]>(SAVED_SEARCHES_QUERY_KEY, (current = []) =>
        current.map((search) => (search.id === updatedSearch.id ? updatedSearch : search)),
      );
    },
  });

  const updateSettingsMutation = useMutation({
    mutationFn: (cadence: NotificationCadence) => updateSavedSearchSettings({ defaultCadence: cadence }),
    onMutate: async (cadence: NotificationCadence) => {
      await queryClient.cancelQueries({ queryKey: SAVED_SEARCH_SETTINGS_QUERY_KEY });
      const previous = queryClient.getQueryData<SavedSearchSettings>(SAVED_SEARCH_SETTINGS_QUERY_KEY);
      queryClient.setQueryData<SavedSearchSettings>(SAVED_SEARCH_SETTINGS_QUERY_KEY, { defaultCadence: cadence });
      return { previous };
    },
    onError: (_error, _cadence, context) => {
      if (context?.previous) {
        queryClient.setQueryData(SAVED_SEARCH_SETTINGS_QUERY_KEY, context.previous);
      }
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: SAVED_SEARCH_SETTINGS_QUERY_KEY });
    },
  });

  const savedSearches = savedSearchesQuery.data ?? [];
  const defaultCadence = settingsQuery.data?.defaultCadence ?? FALLBACK_CADENCE;
  const mostRecent = savedSearches.at(0) ?? null;
  const savedSearchesError = savedSearchesQuery.isError
    ? savedSearchesQuery.error instanceof Error
      ? savedSearchesQuery.error
      : new Error('Failed to load saved searches')
    : null;
  const settingsError = settingsQuery.isError
    ? settingsQuery.error instanceof Error
      ? settingsQuery.error
      : new Error('Failed to load saved search settings')
    : null;

  const addSearch = useCallback(
    (input: AddSavedSearchInput) => addSearchMutation.mutate(input),
    [addSearchMutation],
  );

  const removeSearch = useCallback(
    (id: string) => removeSearchMutation.mutate(id),
    [removeSearchMutation],
  );

  const updateCadence = useCallback(
    (id: string, cadence: NotificationCadence) => updateCadenceMutation.mutate({ id, cadence }),
    [updateCadenceMutation],
  );

  const setDefaultCadence = useCallback(
    (cadence: NotificationCadence) => updateSettingsMutation.mutate(cadence),
    [updateSettingsMutation],
  );

  const setPushEnabled = useCallback((enabled: boolean) => {
    if (enabled && typeof Notification !== 'undefined' && Notification.requestPermission) {
      Notification.requestPermission().catch((error) => {
        console.warn('[saved-searches] Notification permission request failed', error);
      });
    }
    setPushEnabledState(enabled);
    writeLocalPushPreference(enabled);
    toast.success(enabled ? 'Push alerts enabled for saved searches' : 'Push alerts disabled');
  }, []);

  const shareSavedSearch = useCallback(
    async (search: SavedSearch) => {
      const params = new URLSearchParams();
      if (search.query) params.set('query', search.query);
      if (search.location) params.set('location', search.location);
      if (search.jobType && search.jobType !== 'all') params.set('jobType', search.jobType);
      if (search.returnerFriendly) params.set('returnerFriendly', 'true');
      if (search.remoteOnly) params.set('remoteOnly', 'true');
      if (search.tags?.length) params.set('tags', search.tags.join(','));
      if (typeof search.minPay === 'number') params.set('minPay', String(search.minPay));
      if (typeof search.maxPay === 'number') params.set('maxPay', String(search.maxPay));
      const base =
        (typeof window !== 'undefined' && window.location?.origin) || 'https://jobs.lighthouse.careers';
      const link = `${base}/jobs?${params.toString()}`;

      try {
        if (typeof navigator !== 'undefined' && navigator.share) {
          await navigator.share({ title: search.name, url: link });
          toast.success('Share sheet opened');
          return;
        }
        if (typeof navigator !== 'undefined' && navigator.clipboard?.writeText) {
          await navigator.clipboard.writeText(link);
          toast.success('Saved search link copied');
          return;
        }
      } catch (error) {
        console.warn('[saved-searches] Share failed', error);
      }
      toast.error('Unable to share this search right now.');
    },
    [],
  );

  return {
    savedSearches,
    mostRecent,
    addSearch,
    addSearchAsync: addSearchMutation.mutateAsync,
    removeSearch,
    updateCadence,
    defaultCadence,
    setDefaultCadence,
    savedSearchesLoading: savedSearchesQuery.isPending,
    savedSearchesError,
    settingsLoading: settingsQuery.isPending,
    settingsError,
    shareSavedSearch,
    pushEnabled,
    setPushEnabled,
  };
}

export type {
  SavedSearch,
  SavedSearchFilters,
  NotificationCadence,
  SavedSearchSettings,
  AddSavedSearchInput,
};
